import { Injectable } from '@angular/core';
import { Annotation } from '@core/models/annotation.model';
import { Ki67PointsWithData } from '@core/models/ki67points-with-data.model';
import { Ki67Points } from '@core/models/ki67points.model';
import { Ki67Status } from '@core/models/ki67status.model';
import { Point } from '@core/models/point.model';
import { TileInsideAnnotation } from '@core/models/tile-inside-annotation.model';
import { TileRegion } from '@core/models/tile-region.model';
import { BehaviorSubject } from 'rxjs';

declare let OpenSeadragon: any;

@Injectable({
  providedIn: 'root',
})
export class Ki67Service {
  ki67DataByAnnotation = new Map();
  newKi67Data = new BehaviorSubject<Ki67Status>(new Ki67Status());
  private currentAnnotation: Annotation | null = null;
  private abortControllers: AbortController[] = [];

  // OpenSeadragon viewer instance for tile visualization
  private viewer: any = null;
  private tileOverlays: any[] = [];

  // Toggle for tile visualization (set to false to disable overlays)
  public showTileOverlays: boolean = false;

  // Progress tracking
  private currentFetched = 0;
  private currentTotal = 0;
  private isRunning = false;

  // Concurrency control for lambda invocations
  private MAX_CONCURRENT_LAMBDAS = 8; // Maximum number of lambda instances to use in parallel
  private runningLambdas = 0;
  private lambdaQueue: (() => Promise<void>)[] = [];
  private readonly MAX_RETRIES = 1; // Maximum number of retries for failed requests

  // Progress observables for components to subscribe to
  public progress = new BehaviorSubject<{
    current: number;
    total: number;
    running: boolean;
  }>({
    current: 0,
    total: 0,
    running: false,
  });

  /**
   * Set the OpenSeadragon viewer instance for tile visualization
   */
  setViewer(viewer: any) {
    this.viewer = viewer;
  }

  /**
   * Configure the maximum number of concurrent lambda invocations
   */
  setMaxConcurrentLambdas(maxConcurrent: number) {
    if (maxConcurrent > 0) {
      this.MAX_CONCURRENT_LAMBDAS = maxConcurrent;
    }
  }

  /**
   * Clear all tile visualization overlays
   */
  clearTileOverlays() {
    if (this.tileOverlays && this.tileOverlays.length > 0) {
      this.tileOverlays.forEach((element) => {
        try {
          if (this.viewer && this.viewer.removeOverlay) {
            this.viewer.removeOverlay(element);
          }
        } catch (error) {
          console.warn('Error removing tile overlay:', error);
        }
      });
      this.tileOverlays = [];
      console.log('Cleared all tile overlays');
    }
  }

  /**
   * Draw a tile rectangle on the OpenSeadragon viewer
   */
  drawTileRectangle(
    tileRegion: TileRegion,
    level: number,
    col: number,
    row: number,
    subtileInfo: string = '',
  ) {
    if (!this.showTileOverlays) {
      return; // Tile visualization is disabled
    }

    if (!this.viewer) {
      console.warn('No OpenSeadragon viewer available for tile visualization');
      return;
    }

    try {
      // Get the tiled image to understand the coordinate system
      const tiledImage = this.viewer.world.getItemAt(0);
      if (!tiledImage) {
        console.warn('No tiled image available for coordinate conversion');
        return;
      }

      // TileRegion coordinates are in percentage (0-1) relative to the full image
      // We need to convert them to image coordinates, then to viewport coordinates
      const imageSize = tiledImage.getContentSize();

      // Convert from normalized coordinates (0-1) to image coordinates
      const imageX = tileRegion.x[0] * imageSize.x;
      const imageY = tileRegion.y[0] * imageSize.y;
      const imageWidth = (tileRegion.x[1] - tileRegion.x[0]) * imageSize.x;
      const imageHeight = (tileRegion.y[1] - tileRegion.y[0]) * imageSize.y;

      // Convert from image coordinates to viewport coordinates using the correct API
      const viewportRect = tiledImage.imageToViewportRectangle(
        imageX,
        imageY,
        imageWidth,
        imageHeight,
      );

      // Create a rectangle element
      const rectElement = document.createElement('div');
      rectElement.style.border = '3px solid #FF0000';
      rectElement.style.background = 'rgba(255, 0, 0, 0.3)';
      rectElement.style.borderRadius = '0px';
      rectElement.style.boxSizing = 'border-box';
      rectElement.style.pointerEvents = 'none';
      rectElement.style.position = 'absolute';
      rectElement.style.zIndex = '1000';
      rectElement.style.minWidth = '2px';
      rectElement.style.minHeight = '2px';

      // Add tile info as a data attribute
      const tileInfo = subtileInfo
        ? `L${level}_C${col}_R${row}_${subtileInfo}`
        : `L${level}_C${col}_R${row}`;
      rectElement.setAttribute('data-tile-info', tileInfo);

      // Validate the viewport coordinates
      if (
        !viewportRect ||
        isNaN(viewportRect.x) ||
        isNaN(viewportRect.y) ||
        isNaN(viewportRect.width) ||
        isNaN(viewportRect.height) ||
        viewportRect.width <= 0 ||
        viewportRect.height <= 0
      ) {
        console.warn(
          'Invalid viewport coordinates, falling back to simple method',
        );

        // Fallback to simple coordinate mapping
        const location = new OpenSeadragon.Rect(
          tileRegion.x[0],
          tileRegion.y[0],
          tileRegion.x[1] - tileRegion.x[0],
          tileRegion.y[1] - tileRegion.y[0],
        );
        this.viewer.addOverlay(rectElement, location);
      } else {
        // Create OpenSeadragon Rect for the location using viewport coordinates
        const location = new OpenSeadragon.Rect(
          viewportRect.x,
          viewportRect.y,
          viewportRect.width,
          viewportRect.height,
        );

        // Add overlay to viewer using the correct API (element first, then location)
        this.viewer.addOverlay(rectElement, location);
      }

      // Store reference for cleanup
      this.tileOverlays.push(rectElement);
    } catch (error) {
      console.error('Error drawing tile rectangle:', error);
    }
  }

  /**
   * Draw all tiles for visualization (called when Ki67 analysis starts)
   * Note: Individual subtiles will be drawn during processing, so we just clear existing overlays here
   */
  drawAllTiles(tiles: TileInsideAnnotation[]) {
    if (!this.viewer) {
      return;
    }

    // Clear existing overlays - subtiles will be drawn individually during processing
    this.clearTileOverlays();
  }

  processPointArray(rawArray: any[]) {
    return rawArray.map((r) => new Ki67PointsWithData(r[0], r[1], r[2], r[3]));
  }

  computeIndex(rawStatus: Ki67Status) {
    this.separatePositiveAndNegative(rawStatus);

    const filteredPositivePoints = rawStatus.positivePoints.filter(
      (p) => p.size >= rawStatus.minSize,
    );
    const filteredNegativePoints = rawStatus.negativePoints.filter(
      (p) => p.size >= rawStatus.minSize,
    );

    if (filteredPositivePoints.length + filteredNegativePoints.length > 0) {
      rawStatus.ki67Index =
        100.0 *
        (filteredPositivePoints.length /
          (filteredPositivePoints.length + filteredNegativePoints.length));
    }
  }

  findOptimalThreshold(values: number[]): number {
    /**
     * Find optimal threshold for a set of intensity values using Otsu-like algorithm
     * Converts the Python implementation to TypeScript
     */
    if (values.length < 3) {
      return values.length > 0
        ? values.reduce((a, b) => a + b) / values.length
        : 0.12;
    }

    // Create histogram with 50 bins
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const binCount = 50;
    const binSize = (maxVal - minVal) / binCount;

    // Calculate bin centers
    const binCenters: number[] = [];
    for (let i = 0; i < binCount; i++) {
      binCenters.push(minVal + (i + 0.5) * binSize);
    }

    let bestThreshold = values.reduce((a, b) => a + b) / values.length; // mean
    let minVariance = Infinity;

    for (const threshold of binCenters) {
      const lowVals = values.filter((v) => v <= threshold);
      const highVals = values.filter((v) => v > threshold);

      if (lowVals.length > 0 && highVals.length > 0) {
        // Calculate variance for low group
        const meanLow = lowVals.reduce((a, b) => a + b) / lowVals.length;
        const varLow =
          lowVals.reduce((sum, val) => sum + Math.pow(val - meanLow, 2), 0) /
          lowVals.length;

        // Calculate variance for high group
        const meanHigh = highVals.reduce((a, b) => a + b) / highVals.length;
        const varHigh =
          highVals.reduce((sum, val) => sum + Math.pow(val - meanHigh, 2), 0) /
          highVals.length;

        const totalVar = varLow * lowVals.length + varHigh * highVals.length;

        if (totalVar < minVariance) {
          minVariance = totalVar;
          bestThreshold = threshold;
        }
      }
    }

    return Math.max(bestThreshold, 0.12);
  }

  computeOptimalMinIntensity(status: Ki67Status) {
    /**
     * Compute optimal minimum intensity threshold using all available points
     */
    if (status.allPoints.length === 0) {
      status.minIntensity = 0.12; // default fallback
      return;
    }

    // Extract all intensity values from all points
    const intensityValues = status.allPoints.map((point) => point.intensity);

    // Calculate optimal threshold using the Otsu-like algorithm
    status.minIntensity = this.findOptimalThreshold(intensityValues);
  }

  separatePositiveAndNegative(status: Ki67Status) {
    const positivePoints = status.allPoints.filter(
      (s) => s.intensity >= status.minIntensity,
    );
    const negativePoints = status.allPoints.filter(
      (s) => s.intensity < status.minIntensity,
    );
    status.positivePoints = positivePoints;
    status.negativePoints = negativePoints;
  }

  setCurrentAnnotation(annotation: any) {
    this.currentAnnotation = annotation;
  }

  updateStatus(
    status: Ki67Points | null,
    imageId: number,
    annotationId: number,
    requestId: string,
  ) {
    if (status == null) {
      const cancelData = {
        imageId: imageId,
        annotationId: annotationId,
        requisitionId: requestId,
        allPoints: [],
        positivePoints: [],
        negativePoints: [],
        ki67Index: -1,
        minIntensity: -Infinity,
        minSize: -Infinity,
      };

      const key = `${imageId}_${annotationId}`;
      this.ki67DataByAnnotation.set(key, cancelData);
      this.newKi67Data.next(cancelData);
      return;
    }
    if (status.all_points === undefined || status.all_points === null) {
      status.all_points = [];
    }

    if (status.all_points === undefined || status.all_points === null) {
      status.all_points = [];
    }

    // Process and filter points using the current annotation
    let processedPoints = this.processPointArray(status.all_points);

    // Filter points to only include those inside the annotation
    if (this.currentAnnotation) {
      processedPoints = this.filterPointsInsideAnnotation(
        processedPoints,
        this.currentAnnotation,
      );
    }

    const key = `${imageId}_${annotationId}`;
    if (this.ki67DataByAnnotation.has(key)) {
      if (this.ki67DataByAnnotation.get(key).requisitionId !== requestId) {
        const newData = new Ki67Status();
        newData.annotationId = annotationId;
        newData.requisitionId = requestId;
        newData.imageId = imageId;
        newData.allPoints = processedPoints;

        // Calculate optimal threshold and trigger visual update
        this.computeOptimalMinIntensity(newData);
        this.ki67DataByAnnotation.set(key, newData);

        // Use updateMinIntensity to ensure visual rendering updates
        this.updateMinIntensity(imageId, annotationId, newData.minIntensity);
      } else {
        const currentData = this.ki67DataByAnnotation.get(key);
        currentData.allPoints = currentData.allPoints.concat(processedPoints);

        // Calculate optimal threshold and trigger visual update
        this.computeOptimalMinIntensity(currentData);

        // Use updateMinIntensity to ensure visual rendering updates
        this.updateMinIntensity(
          imageId,
          annotationId,
          currentData.minIntensity,
        );
      }
    } else {
      const newData = new Ki67Status();
      newData.annotationId = annotationId;
      newData.requisitionId = requestId;
      newData.imageId = imageId;
      newData.allPoints = processedPoints;

      // Calculate optimal threshold and trigger visual update
      this.computeOptimalMinIntensity(newData);
      this.ki67DataByAnnotation.set(key, newData);

      // Use updateMinIntensity to ensure visual rendering updates
      this.updateMinIntensity(imageId, annotationId, newData.minIntensity);
    }
  }

  updateMinIntensity(
    imageId: number,
    annotationId: number,
    minIntensity: number,
  ) {
    const key = `${imageId}_${annotationId}`;
    const toChange = this.ki67DataByAnnotation.get(key);
    toChange.minIntensity = minIntensity;
    this.computeIndex(toChange);
    this.newKi67Data.next(toChange);
  }

  updateMinSize(imageId: number, annotationId: number, minSize: number) {
    const key = `${imageId}_${annotationId}`;
    const toChange = this.ki67DataByAnnotation.get(key);
    toChange.minSize = minSize;
    this.computeIndex(toChange);
    this.newKi67Data.next(toChange);
  }

  getForAnnotation(imageId: number, annotationId: number) {
    const key = `${imageId}_${annotationId}`;
    if (this.ki67DataByAnnotation.has(key)) {
      return this.ki67DataByAnnotation.get(key);
    }
    return null;
  }

  isPointInPolygon(
    point: { x: number; y: number },
    polygonPoints: Point[],
  ): boolean {
    /**
     * Ray casting algorithm to determine if a point is inside a polygon
     * Based on the same logic as backend's filter_points_inside using Shapely
     */
    if (polygonPoints.length < 3) {
      return false; // A polygon must have at least 3 points
    }

    const x = point.x;
    const y = point.y;
    let inside = false;

    for (
      let i = 0, j = polygonPoints.length - 1;
      i < polygonPoints.length;
      j = i++
    ) {
      const xi = polygonPoints[i].x;
      const yi = polygonPoints[i].y;
      const xj = polygonPoints[j].x;
      const yj = polygonPoints[j].y;

      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }

    return inside;
  }

  filterPointsInsideAnnotation(
    ki67Points: Ki67PointsWithData[],
    annotation: any,
  ): Ki67PointsWithData[] {
    /**
     * Filter Ki67 points to only include those inside the annotation polygon
     * Replaces the backend's filter_points_inside functionality
     */
    if (!annotation) {
      return []; // Invalid annotation
    }

    let polygonPoints: Point[];

    // Handle different annotation types
    if (annotation.type === 'free_annotation') {
      if (!annotation.points || annotation.points.length < 3) {
        return []; // Invalid free annotation
      }
      polygonPoints = annotation.points;
    } else if (annotation.type === 'square_annotation') {
      // Convert square annotation corners to points array
      if (
        !annotation.start ||
        !annotation.corner3 ||
        !annotation.end ||
        !annotation.corner4
      ) {
        return []; // Invalid square annotation
      }
      polygonPoints = [
        annotation.start,
        annotation.corner3,
        annotation.end,
        annotation.corner4,
      ];
    } else {
      return []; // Unknown annotation type
    }

    return ki67Points.filter((point) =>
      this.isPointInPolygon({ x: point.x, y: point.y }, polygonPoints),
    );
  }

  /**
   * Check if a rectangular region intersects with the annotation polygon
   */
  isRegionIntersectingAnnotation(region: TileRegion, annotation: any): boolean {
    if (!annotation) {
      return false;
    }

    let polygonPoints: Point[];

    // Handle different annotation types
    if (annotation.type === 'free_annotation') {
      if (!annotation.points || annotation.points.length < 3) {
        return false;
      }
      polygonPoints = annotation.points;
    } else if (annotation.type === 'square_annotation') {
      if (
        !annotation.start ||
        !annotation.corner3 ||
        !annotation.end ||
        !annotation.corner4
      ) {
        return false;
      }
      polygonPoints = [
        annotation.start,
        annotation.corner3,
        annotation.end,
        annotation.corner4,
      ];
    } else {
      return false;
    }

    // Convert region to rectangle corners
    const regionCorners = [
      { x: region.x[0] * 100, y: region.y[0] * 100 }, // Top-left
      { x: region.x[1] * 100, y: region.y[0] * 100 }, // Top-right
      { x: region.x[1] * 100, y: region.y[1] * 100 }, // Bottom-right
      { x: region.x[0] * 100, y: region.y[1] * 100 }, // Bottom-left
    ];

    // Check if any corner of the region is inside the annotation
    const hasCornerInside = regionCorners.some((corner) =>
      this.isPointInPolygon(corner, polygonPoints),
    );

    if (hasCornerInside) {
      return true;
    }

    // Check if any annotation point is inside the region rectangle
    const hasAnnotationPointInside = polygonPoints.some((point) => {
      const x = point.x / 100; // Convert back to 0-1 range
      const y = point.y / 100;
      return (
        x >= region.x[0] &&
        x <= region.x[1] &&
        y >= region.y[0] &&
        y <= region.y[1]
      );
    });

    if (hasAnnotationPointInside) {
      return true;
    }

    // Check for edge intersections (simplified check)
    // This is a basic implementation - could be enhanced for more precise edge intersection detection
    return false;
  }

  async runKi67Analysis(
    imageId: number,
    annotationId: number,
    minDistance: number,
    csrfToken: string,
    requisitionId: string,
    dziUrl: string,
    environment: any,
    annotation: any,
    imageMetadata: any,
  ): Promise<void> {
    try {
      this.isRunning = true;
      this.currentFetched = 0;
      this.currentTotal = 0;
      this.runningLambdas = 0;
      this.lambdaQueue = [];
      this.updateProgress();

      // Set current annotation for point filtering
      this.setCurrentAnnotation(annotation);

      // Get DZI parameters
      const dziParams = await this.getDziParams(dziUrl, csrfToken);
      const tileSize = dziParams.tileSize;

      // Calculate tiles for annotation
      const tiles = this.calculateTilesForAnnotation(
        annotation,
        dziParams,
        imageMetadata,
      );
      this.currentTotal = tiles.length;
      this.updateProgress();

      if (tiles.length === 0) {
        throw new Error('No tiles found for annotation');
      }

      // Draw tile visualization on OpenSeadragon viewer
      this.drawAllTiles(tiles);

      // Process all tiles directly (concurrency control happens at subtile level)
      const tilePromises = tiles.map((tile, index) =>
        this.processKi67Tile(
          imageId,
          annotationId,
          dziUrl,
          tile.l,
          tile.c,
          tile.r,
          tile.region,
          minDistance,
          requisitionId,
          index + 1,
          tiles.length,
          environment,
          tileSize,
        ),
      );

      await Promise.allSettled(tilePromises);

      this.isRunning = false;
      this.updateProgress();
    } catch (error: any) {
      console.error('Error in Ki67 analysis:', error);
      this.isRunning = false;
      this.updateProgress();
      throw error;
    }
  }

  private startConcurrentProcessing(): void {
    while (
      this.lambdaQueue.length > 0 &&
      this.runningLambdas < this.MAX_CONCURRENT_LAMBDAS
    ) {
      const task = this.lambdaQueue.shift();
      if (task) {
        this.runningLambdas++;
        task();
      }
    }
  }

  private processNextInQueue(): void {
    if (
      this.lambdaQueue.length > 0 &&
      this.runningLambdas < this.MAX_CONCURRENT_LAMBDAS
    ) {
      const task = this.lambdaQueue.shift();
      if (task) {
        this.runningLambdas++;
        task();
      }
    }
  }

  private createRetryableTask(
    operation: () => Promise<void>,
    taskName: string,
    resolve: () => void,
    reject: (error: any) => void,
    retryCount: number = 0,
  ): () => Promise<void> {
    return async () => {
      try {
        await operation();
        resolve();
      } catch (error) {
        if (retryCount < this.MAX_RETRIES) {
          console.warn(
            `Retrying ${taskName} (attempt ${retryCount + 1}/${this.MAX_RETRIES + 1}):`,
            error,
          );

          // Create a new retry task and add it to the queue
          const retryTask = this.createRetryableTask(
            operation,
            taskName,
            resolve,
            reject,
            retryCount + 1,
          );

          this.lambdaQueue.push(retryTask);
          this.processNextInQueue();
        } else {
          console.error(
            `Failed ${taskName} after ${this.MAX_RETRIES + 1} attempts:`,
            error,
          );
          reject(error);
        }
      } finally {
        this.runningLambdas--;
        this.processNextInQueue();
      }
    };
  }

  async processSubtilesWithConcurrencyControl(
    validSubtiles: any[],
    allSubtiles: any[],
    subtileBlobs: { blob: Blob; subtileIndex: number }[],
    imageId: number,
    annotationId: number,
    level: number,
    col: number,
    row: number,
    minDistance: number,
    environment: any,
  ): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const results: any[] = [];
      let completed = 0;

      // Create a task for each valid subtile with retry logic
      const tasks = validSubtiles.map((subtile: any, validIndex: number) => {
        const taskResolve = () => {
          completed++;
          // Check if all tasks are completed
          if (completed === validSubtiles.length) {
            resolve(results);
          }
        };

        const taskReject = (error: any) => {
          console.error(`Subtile ${validIndex} failed after retries:`, error);
          results[validIndex] = { all_points: [] }; // Empty result on error
          completed++;
          // Check if all tasks are completed
          if (completed === validSubtiles.length) {
            resolve(results);
          }
        };

        return this.createRetryableTask(
          async () => {
            // Find the original index of this subtile in the full subtiles array
            const subtileIndex = allSubtiles.findIndex((s) => s === subtile);

            const subtileBlob = subtileBlobs.find(
              (sb) => sb.subtileIndex === subtileIndex,
            )?.blob;
            if (!subtileBlob) {
              throw new Error(`Missing subtile blob for index ${subtileIndex}`);
            }

            const result = await this.sendTileToBackend(
              imageId,
              annotationId,
              subtileBlob,
              level,
              col,
              row,
              subtile.region,
              minDistance,
              environment,
            );

            results[validIndex] = result;
          },
          `subtile ${validIndex}`,
          taskResolve,
          taskReject,
        );
      });

      // Add all tasks to the queue
      this.lambdaQueue.push(...tasks);

      // Start processing up to MAX_CONCURRENT_LAMBDAS tasks
      this.startConcurrentProcessing();
    });
  }

  async processKi67Tile(
    imageId: number,
    annotationId: number,
    dziUrl: string,
    level: number,
    col: number,
    row: number,
    tileRegion: TileRegion,
    minDistance: number,
    requisitionId: string,
    index: number,
    numTiles: number,
    environment: any,
    tileSize: number,
  ): Promise<void> {
    try {
      // Build tile URL
      // Remove .dzi extension if URL ends with a number.dzi
      dziUrl = dziUrl.replace(/(\d+)\.dzi$/, '$1');
      const tileUrl = `${dziUrl}_files/${level}/${col}_${row}.jpeg`;

      // Determine if subdivision is needed
      const needsSubdivision = tileSize >= 512;

      if (!needsSubdivision) {
        // Check if single tile intersects with annotation
        const intersects = this.isRegionIntersectingAnnotation(
          tileRegion,
          this.currentAnnotation,
        );

        if (intersects) {
          // Draw single tile visualization
          this.drawTileRectangle(tileRegion, level, col, row);

          // Process single tile
          await this.processSingleTile(
            imageId,
            annotationId,
            tileUrl,
            level,
            col,
            row,
            tileRegion,
            minDistance,
            requisitionId,
            environment,
          );
        }
      } else {
        // Process subdivided tile (subtiles will be drawn in processSubdividedTile)
        await this.processSubdividedTile(
          imageId,
          annotationId,
          tileUrl,
          level,
          col,
          row,
          tileRegion,
          minDistance,
          requisitionId,
          environment,
          tileSize,
        );
      }

      this.currentFetched++;
      this.updateProgress();
    } catch (error: any) {
      console.error(`Error processing tile (${col},${row}):`, error);
      this.currentFetched++;
      this.updateProgress();
    }
  }

  async processSingleTile(
    imageId: number,
    annotationId: number,
    tileUrl: string,
    level: number,
    col: number,
    row: number,
    tileRegion: TileRegion,
    minDistance: number,
    requisitionId: string,
    environment: any,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Create a task for the single tile lambda request with retry logic
      const task = this.createRetryableTask(
        async () => {
          // Fetch tile image
          const imageResponse = await fetch(tileUrl);
          if (!imageResponse.ok) {
            throw new Error(
              `Failed to fetch tile image: ${imageResponse.status}`,
            );
          }
          const imageBlob = await imageResponse.blob();

          // Send to backend for processing
          const data = await this.sendTileToBackend(
            imageId,
            annotationId,
            imageBlob,
            level,
            col,
            row,
            tileRegion,
            minDistance,
            environment,
          );

          // Update status with results
          this.updateStatus(data, imageId, annotationId, requisitionId);
        },
        `single tile (${col},${row})`,
        resolve,
        reject,
      );

      // Add task to queue
      this.lambdaQueue.push(task);

      // Start processing if there's capacity
      this.startConcurrentProcessing();
    });
  }

  async processSubdividedTile(
    imageId: number,
    annotationId: number,
    tileUrl: string,
    level: number,
    col: number,
    row: number,
    tileRegion: TileRegion,
    minDistance: number,
    requisitionId: string,
    environment: any,
    tileSize: number,
  ): Promise<void> {
    // Fetch tile image
    const imageResponse = await fetch(tileUrl);
    if (!imageResponse.ok) {
      throw new Error(`Failed to fetch tile image: ${imageResponse.status}`);
    }
    const imageBlob = await imageResponse.blob();

    // Subdivide tile
    const subtiles = this.subdivideTile(
      tileUrl,
      level,
      col,
      row,
      tileRegion,
      tileSize,
    );

    // Filter subtiles to only process those that intersect with the annotation
    const validSubtiles = subtiles.filter(
      (subtile: any, subtileIndex: number) => {
        return this.isRegionIntersectingAnnotation(
          subtile.region,
          this.currentAnnotation,
        );
      },
    );

    // Draw each valid subtile for visualization
    validSubtiles.forEach((subtile: any, subtileIndex: number) => {
      this.drawTileRectangle(
        subtile.region,
        level,
        col,
        row,
        `S${subtileIndex}`, // Add subtile index to distinguish them
      );
    });

    // If no valid subtiles, skip processing
    if (validSubtiles.length === 0) {
      return;
    }

    // Crop image into subtiles (still need all subtiles for cropping)
    const subtileBlobs = await this.cropImageToSubtiles(imageBlob, tileSize);

    // Process only the valid subtiles with concurrency control
    const subtileResults = await this.processSubtilesWithConcurrencyControl(
      validSubtiles,
      subtiles,
      subtileBlobs,
      imageId,
      annotationId,
      level,
      col,
      row,
      minDistance,
      environment,
    );

    // Merge subtile results
    const mergedData: any = {
      all_points: [],
    };

    for (const subtileData of subtileResults) {
      if (subtileData.all_points) {
        mergedData.all_points.push(...subtileData.all_points);
      }
    }

    // Update status with merged results
    this.updateStatus(mergedData, imageId, annotationId, requisitionId);
  }

  async sendTileToBackend(
    imageId: number,
    annotationId: number,
    imageBlob: Blob,
    level: number,
    col: number,
    row: number,
    tileRegion: TileRegion,
    minDistance: number,
    environment: any,
  ): Promise<any> {
    const controller = new AbortController();
    this.abortControllers.push(controller);

    const route = `${environment.ki67ServiceUrl}/ki67_points/${imageId}/${annotationId}/${level}/${col}_${row}?min_distance=${minDistance}`;

    // Create FormData to send binary image
    const formData = new FormData();
    formData.append('image', imageBlob, `tile_${level}_${col}_${row}.jpeg`);
    formData.append('region_data', JSON.stringify(tileRegion));

    try {
      // Send binary image data to lambda function (without custom headers to avoid CORS preflight)
      const response = await fetch(route, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(
          `HTTP ${response.status}: ${response.statusText} - ${errorText}`,
        );
      }

      return await response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request was cancelled');
      }
      console.error(`Error sending tile to backend (${col},${row}):`, error);
      throw error;
    }
  }

  calculateTilesForAnnotation(
    annotation: any,
    dziParams: any,
    imageMetadata: any,
  ): TileInsideAnnotation[] {
    const tileSize = dziParams.tileSize;
    const overlap = dziParams.overlap;
    const levelOffset = 0;

    // Parse annotation points based on type
    let points: number[][];
    if (annotation.type === 'free_annotation') {
      points = annotation.points.map((point: any) => [point.x, point.y]);
    } else if (annotation.type === 'square_annotation') {
      const corners = [
        annotation.start,
        annotation.corner3,
        annotation.end,
        annotation.corner4,
      ];
      points = corners.map((corner: any) => [corner.x, corner.y]);
    } else {
      return [];
    }

    // Get image bounds
    const bounds_x = imageMetadata.bounds_x;
    const bounds_y = imageMetadata.bounds_y;

    // Calculate target level
    const max_level = Math.ceil(Math.log2(Math.max(bounds_x, bounds_y)));
    const target_level = max_level + levelOffset;

    if (target_level < 0) {
      console.error(
        `Invalid level offset. Resulting level ${target_level} is below 0.`,
      );
      return [];
    }

    // Compute the scale factor for the target level
    const scale_factor = Math.pow(2, max_level - target_level);
    const level_bounds_x = Math.ceil(bounds_x / scale_factor);
    const level_bounds_y = Math.ceil(bounds_y / scale_factor);

    // Compute annotation bounds in normalized coordinates, then convert to target level pixels
    const min_x = (Math.min(...points.map((p) => p[0])) * level_bounds_x) / 100;
    const max_x = (Math.max(...points.map((p) => p[0])) * level_bounds_x) / 100;
    const min_y = (Math.min(...points.map((p) => p[1])) * level_bounds_y) / 100;
    const max_y = (Math.max(...points.map((p) => p[1])) * level_bounds_y) / 100;

    const min_column_idx = Math.floor(min_x / tileSize);
    const max_column_idx = Math.ceil(max_x / tileSize);
    const min_row_idx = Math.floor(min_y / tileSize);
    const max_row_idx = Math.ceil(max_y / tileSize);

    const tile_data: TileInsideAnnotation[] = [];

    for (let c = min_column_idx; c < max_column_idx; c++) {
      for (let r = min_row_idx; r < max_row_idx; r++) {
        const x0 = Math.max(c * tileSize - overlap, 0);
        const y0 = Math.max(r * tileSize - overlap, 0);
        const x1 = Math.min((c + 1) * tileSize + overlap, level_bounds_x);
        const y1 = Math.min((r + 1) * tileSize + overlap, level_bounds_y);

        const address: TileInsideAnnotation = {
          l: target_level,
          c: c,
          r: r,
          region: {
            x: [x0 / level_bounds_x, x1 / level_bounds_x],
            y: [y0 / level_bounds_y, y1 / level_bounds_y],
          },
        };

        tile_data.push(address);
      }
    }

    return tile_data;
  }

  async getDziParams(dziUrl: string, csrfToken: string) {
    const controller = new AbortController();
    this.abortControllers.push(controller);

    try {
      const response = await fetch(dziUrl, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error: ${response.status} - ${response.statusText}`,
        );
      }

      const xmlText = await response.text();

      // Simple regex to get attributes from <Image>
      const tileSizeMatch = xmlText.match(/TileSize="(\d+)"/);
      const overlapMatch = xmlText.match(/Overlap="(\d+)"/);

      if (!tileSizeMatch || !overlapMatch) {
        return { tileSize: 1024, overlap: 0 };
      }

      const tileSize = parseInt(tileSizeMatch[1], 10);
      const overlap = parseInt(overlapMatch[1], 10);

      return { tileSize, overlap };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('DZI request was cancelled');
      }
      console.error('Error fetching DZI params:', error);
      throw error;
    }
  }

  subdivideTile(
    tileUrl: string,
    level: number,
    col: number,
    row: number,
    tileRegion: TileRegion,
    tileSize: number,
  ): Array<{ url: string; region: TileRegion }> {
    // If no subdivision needed for small tiles, return original tile
    if (tileSize >= 256 && tileSize <= 258) {
      return [{ url: tileUrl, region: tileRegion }];
    }

    // For larger tiles, we need to check actual tile dimensions
    // Since we don't have the actual image dimensions here, we'll determine
    // subdivision based on the configured tile size and apply it in cropImageToSubtiles

    // Calculate potential subdivisions for each axis
    let numSubdivisionsX = 1;
    let numSubdivisionsY = 1;

    if (tileSize >= 512 && tileSize <= 514) {
      // For 512px tiles, try 2x2 subdivision
      numSubdivisionsX = 2;
      numSubdivisionsY = 2;
    } else if (tileSize >= 1024 && tileSize <= 1026) {
      // For 1024px tiles, try 4x4 subdivision
      numSubdivisionsX = 4;
      numSubdivisionsY = 4;
    } else {
      console.warn(
        `Unexpected tile size: ${tileSize}px. Using no subdivision.`,
      );
      return [{ url: tileUrl, region: tileRegion }];
    }

    // If no subdivision needed, return original tile
    if (numSubdivisionsX === 1 && numSubdivisionsY === 1) {
      return [{ url: tileUrl, region: tileRegion }];
    }

    const subdivisions: Array<{ url: string; region: TileRegion }> = [];
    const subtileSizeX = tileSize / numSubdivisionsX;
    const subtileSizeY = tileSize / numSubdivisionsY;

    for (let subRow = 0; subRow < numSubdivisionsY; subRow++) {
      for (let subCol = 0; subCol < numSubdivisionsX; subCol++) {
        // Calculate subtile region within the original tile
        const subTileStartX = (subCol * subtileSizeX) / tileSize;
        const subTileEndX = ((subCol + 1) * subtileSizeX) / tileSize;
        const subTileStartY = (subRow * subtileSizeY) / tileSize;
        const subTileEndY = ((subRow + 1) * subtileSizeY) / tileSize;

        // Map subtile coordinates to global image coordinates
        const globalStartX =
          tileRegion.x[0] + (tileRegion.x[1] - tileRegion.x[0]) * subTileStartX;
        const globalEndX =
          tileRegion.x[0] + (tileRegion.x[1] - tileRegion.x[0]) * subTileEndX;
        const globalStartY =
          tileRegion.y[0] + (tileRegion.y[1] - tileRegion.y[0]) * subTileStartY;
        const globalEndY =
          tileRegion.y[0] + (tileRegion.y[1] - tileRegion.y[0]) * subTileEndY;

        const subRegion: TileRegion = {
          x: [globalStartX, globalEndX],
          y: [globalStartY, globalEndY],
        };

        subdivisions.push({
          url: tileUrl,
          region: subRegion,
        });
      }
    }

    return subdivisions;
  }

  async cropImageToSubtiles(
    imageBlob: Blob,
    tileSize: number,
  ): Promise<{ blob: Blob; subtileIndex: number }[]> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      img.onload = async () => {
        try {
          const actualWidth = img.width;
          const actualHeight = img.height;

          // Calculate subdivisions based on actual image dimensions and minimum size constraint
          let numSubdivisionsX = 1;
          let numSubdivisionsY = 1;

          // Determine subdivisions for X axis
          if (tileSize >= 512 && tileSize <= 514) {
            // Try 2x subdivision for X axis
            const potentialSubtileWidth = actualWidth / 2;
            if (potentialSubtileWidth >= 120) {
              numSubdivisionsX = 2;
            }
          } else if (tileSize >= 1024 && tileSize <= 1026) {
            // Try 4x subdivision for X axis
            const potentialSubtileWidth = actualWidth / 4;
            if (potentialSubtileWidth >= 120) {
              numSubdivisionsX = 4;
            } else {
              // Try 2x subdivision for X axis
              const potentialSubtileWidth2x = actualWidth / 2;
              if (potentialSubtileWidth2x >= 120) {
                numSubdivisionsX = 2;
              }
            }
          }

          // Determine subdivisions for Y axis
          if (tileSize >= 512 && tileSize <= 514) {
            // Try 2x subdivision for Y axis
            const potentialSubtileHeight = actualHeight / 2;
            if (potentialSubtileHeight >= 120) {
              numSubdivisionsY = 2;
            }
          } else if (tileSize >= 1024 && tileSize <= 1026) {
            // Try 4x subdivision for Y axis
            const potentialSubtileHeight = actualHeight / 4;
            if (potentialSubtileHeight >= 120) {
              numSubdivisionsY = 4;
            } else {
              // Try 2x subdivision for Y axis
              const potentialSubtileHeight2x = actualHeight / 2;
              if (potentialSubtileHeight2x >= 120) {
                numSubdivisionsY = 2;
              }
            }
          }

          // If no subdivision needed, return original image
          if (numSubdivisionsX === 1 && numSubdivisionsY === 1) {
            resolve([{ blob: imageBlob, subtileIndex: 0 }]);
            return;
          }

          const subtileWidth = actualWidth / numSubdivisionsX;
          const subtileHeight = actualHeight / numSubdivisionsY;
          const subtiles: { blob: Blob; subtileIndex: number }[] = [];

          for (let subRow = 0; subRow < numSubdivisionsY; subRow++) {
            for (let subCol = 0; subCol < numSubdivisionsX; subCol++) {
              const subtileIndex = subRow * numSubdivisionsX + subCol;

              // Calculate subtile boundaries
              const startX = subCol * subtileWidth;
              const startY = subRow * subtileHeight;
              const endX = Math.min(startX + subtileWidth, actualWidth);
              const endY = Math.min(startY + subtileHeight, actualHeight);

              const actualSubtileWidth = endX - startX;
              const actualSubtileHeight = endY - startY;

              // Set canvas size for this subtile
              canvas.width = actualSubtileWidth;
              canvas.height = actualSubtileHeight;

              // Draw the cropped portion
              ctx.drawImage(
                img,
                startX,
                startY,
                actualSubtileWidth,
                actualSubtileHeight,
                0,
                0,
                actualSubtileWidth,
                actualSubtileHeight,
              );

              // Convert canvas to blob
              const blob = await new Promise<Blob>(
                (blobResolve, blobReject) => {
                  canvas.toBlob(
                    (result) => {
                      if (result) {
                        blobResolve(result);
                      } else {
                        blobReject(new Error('Failed to create blob'));
                      }
                    },
                    'image/jpeg',
                    0.9,
                  );
                },
              );

              subtiles.push({ blob, subtileIndex });
            }
          }

          resolve(subtiles);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      // Convert blob to data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read image blob'));
      };
      reader.readAsDataURL(imageBlob);
    });
  }

  cancelAllRequests() {
    this.abortControllers.forEach((controller) => {
      controller.abort();
    });
    this.abortControllers.length = 0;
    this.isRunning = false;
    this.currentFetched = 0;
    this.currentTotal = 0;
    this.runningLambdas = 0;
    this.lambdaQueue = [];
    this.updateProgress();
  }

  private updateProgress() {
    this.progress.next({
      current: this.currentFetched,
      total: this.currentTotal,
      running: this.isRunning,
    });
  }

  // Getter for progress string (used by component)
  get progressString(): string {
    return this.currentTotal !== 0
      ? `${this.currentFetched}/${this.currentTotal}`
      : '';
  }

  get running(): boolean {
    return this.isRunning;
  }
}
